import type React from "react";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IBuild } from "../../../model/model";
import { getConnectedDevice, getInstalledPackages } from "../state/deviceInstallerSlice";
import { getSelectedProjectGroup } from "../../../features/projectGroups/state/projectGroupsSlice";
import { installApkFromUrl } from "../utils/adbInstaller";
import { detectDeviceFamily } from "../utils/platformCompat";
import { getBuildInstallStatus } from "../utils/buildStatus";
import { useLaunchApp } from "../hooks/useLaunchApp";
import InstallProgressModal from "./InstallProgressModal";
import MediaPushModal from "./MediaPushModal";
import UninstallButton from "./UninstallButton";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import {
  DeviceIcon,
  WarningCircleIcon,
  DownloadArrowIcon,
  CheckIcon,
  PlayIcon,
  Spinner,
} from "./icons";
import { launchApp } from "../utils/appLauncher";
import { trackEvent } from "../../../trackEvent";

interface Props {
  build: IBuild;
}

const InstallOnDeviceButton = ({ build }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);
  const installedPackages = useAppSelector(getInstalledPackages);
  const selectedPG = useAppSelector(getSelectedProjectGroup);
  const [showModal, setShowModal] = useState(false);
  const [showMediaPushModal, setShowMediaPushModal] = useState(false);

  const isDeviceReady = device?.status === "ready";
  const packageName = build.package_name;
  const deviceFamily = device ? detectDeviceFamily(device.productName) : null;

  const buildStatus = getBuildInstallStatus({
    isDeviceReady: !!isDeviceReady,
    deviceFamily,
    targetPlatform: build.target_platform,
    packageName: packageName ?? null,
    buildVersion: build.version,
    installedPackages,
  });

  const deviceVersionCode = packageName ? installedPackages[packageName]?.versionCode : null;

  const handleInstall = async () => {
    if (!isDeviceReady) return;
    trackEvent("apk-install-device");
    setShowModal(true);
    const fileName = build.name || `wixar_build_${build.id}.apk`;
    await installApkFromUrl(build.s3_url, fileName, build.id, dispatch, packageName || undefined);
  };

  const projectGroupId = build.project_group?.id ?? selectedPG?.id;

  const handleMediaPush = () => {
    if (!packageName || !projectGroupId) return;
    setShowMediaPushModal(true);
  };

  const onLaunch = useCallback(async () => {
    if (!packageName) throw new Error("No package name");
    await launchApp(packageName);
  }, [packageName]);
  const { status: launchStatus, launch: handleLaunch } = useLaunchApp(onLaunch);

  // Build the status-dependent content
  let content: React.ReactNode;

  if (buildStatus === "not_connected") {
    content = (
      <Tooltip message={t("deviceInstaller.noDeviceConnected")} classNameChildren="right-0">
        <button
          disabled
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-not-allowed bg-gray-50 text-gray-400 border border-gray-200"
        >
          <DeviceIcon />
          <span>{t("deviceInstaller.installOnDevice")}</span>
        </button>
      </Tooltip>
    );
  } else if (buildStatus === "incompatible") {
    content = (
      <Tooltip
        message={t("deviceInstaller.incompatibleDevice", { device: device?.productName })}
        classNameChildren="right-0"
      >
        <button
          disabled
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-not-allowed bg-gray-50 text-gray-400 border border-gray-200"
        >
          <DeviceIcon />
          <span>{t("deviceInstaller.installOnDevice")}</span>
        </button>
      </Tooltip>
    );
  } else if (buildStatus === "outdated") {
    content = (
      <div className="flex items-center gap-2">
        <Tooltip
          message={t("deviceInstaller.outdated", {
            device: deviceVersionCode,
            latest: build.version,
          })}
          classNameChildren="right-0"
        >
          <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-orange-50 text-orange-600 border border-orange-200">
            <WarningCircleIcon />V{deviceVersionCode}
          </span>
        </Tooltip>
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer text-white bg-green-500 hover:opacity-90 transition-colors"
        >
          <DownloadArrowIcon />
          {t("deviceInstaller.update")} V{build.version}
        </button>
      </div>
    );
  } else if (buildStatus === "installed") {
    content = (
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-gray-50 text-gray-500 border border-gray-200">
          <CheckIcon className="w-3.5 h-3.5 text-green-500" />V{build.version}
        </span>
        {packageName && (
          <Tooltip
            message={
              launchStatus === "launched"
                ? t("deviceInstaller.launchSuccess")
                : launchStatus === "error"
                ? t("deviceInstaller.retryLaunch")
                : t("deviceInstaller.launchOnDevice")
            }
            classNameChildren="right-0"
          >
            <button
              onClick={handleLaunch}
              disabled={launchStatus === "launching"}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                launchStatus === "launched"
                  ? "bg-green-50 text-green-500 border border-green-200"
                  : launchStatus === "error"
                  ? "bg-red-50 text-red-400 border border-red-200 hover:bg-red-100 cursor-pointer"
                  : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 cursor-pointer"
              }`}
            >
              {launchStatus === "launching" ? (
                <Spinner className="w-3.5 h-3.5" color="green" />
              ) : launchStatus === "launched" ? (
                <CheckIcon className="w-3.5 h-3.5" />
              ) : (
                <PlayIcon className="w-3.5 h-3.5" />
              )}
              {t("deviceInstaller.launchOnDevice")}
            </button>
          </Tooltip>
        )}
        {packageName && projectGroupId && (
          <Tooltip message={t("deviceInstaller.mediaManageButton")} classNameChildren="right-0">
            <button
              onClick={handleMediaPush}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
            >
              <DownloadArrowIcon />
              {t("deviceInstaller.mediaManageButton")}
            </button>
          </Tooltip>
        )}
        {packageName && <UninstallButton packageName={packageName} />}
        {showMediaPushModal && projectGroupId && packageName && (
          <MediaPushModal
            projectGroupId={projectGroupId}
            packageName={packageName}
            onClose={() => setShowMediaPushModal(false)}
          />
        )}
      </div>
    );
  } else {
    // Default: Install
    content = (
      <button
        onClick={handleInstall}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-colors"
      >
        <DeviceIcon />
        <span>{t("deviceInstaller.installOnDevice")}</span>
        {isDeviceReady && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white bg-green-500" />
        )}
      </button>
    );
  }

  return (
    <>
      {content}
      {showModal && <InstallProgressModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default InstallOnDeviceButton;
